import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function RateLimitDashboard() {
  const [keys, setKeys]   = useState([]);
  const [limits, setLimits] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/tenant/api-keys');
      const activeKeys = r.data.filter(k => k.status === 'active');
      setKeys(activeKeys);
      // Fetch rate-limit status for each active key
      const statuses = await Promise.all(
        activeKeys.map(k => api.get(`/tenant/api-keys/${k.id}/rate-limit`).then(res => [k.id, res.data]).catch(() => [k.id, null])),
      );
      setLimits(Object.fromEntries(statuses));
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const Meter = ({ label, used, max }) => {
    const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
    const color = pct > 90 ? '#f38ba8' : pct > 70 ? '#fab387' : '#a6e3a1';
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
          <span style={{ color: 'var(--text-2)' }}>{label}</span>
          <span style={{ fontWeight: 600 }}>{used?.toLocaleString()} / {max?.toLocaleString()}</span>
        </div>
        <div style={{ height: 6, background: 'var(--border)', borderRadius: 4 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width .3s' }} />
        </div>
      </div>
    );
  };

  const S = { card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 } };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Rate Limit Dashboard</h2>
        <button onClick={load} className="btn btn-ghost" style={{ fontSize: 12 }}>↻ Refresh</button>
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading…</div> : (
        keys.length === 0
          ? <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>No active API keys to monitor.</div>
          : keys.map(k => {
            const l = limits[k.id];
            return (
              <div key={k.id} style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <strong style={{ fontSize: 14 }}>{k.name}</strong>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: k.environment === 'production' ? '#a6e3a122' : '#89b4fa22', color: k.environment === 'production' ? '#a6e3a1' : '#89b4fa', letterSpacing: 1 }}>{k.environment?.toUpperCase()}</span>
                </div>
                {l ? (
                  <>
                    <Meter label="Per-minute usage" used={l.calls_last_minute} max={l.rate_limit_per_min} />
                    <Meter label="Daily usage" used={l.calls_today} max={l.rate_limit_per_day} />
                    <div style={{ display: 'flex', gap: 24, marginTop: 14, fontSize: 12, color: 'var(--text-3)' }}>
                      <span>Remaining this minute: <strong style={{ color: 'var(--text-2)' }}>{l.minute_remaining}</strong></span>
                      <span>Remaining today: <strong style={{ color: 'var(--text-2)' }}>{l.day_remaining?.toLocaleString()}</strong></span>
                      <span>Total calls: <strong style={{ color: 'var(--text-2)' }}>{l.total_calls?.toLocaleString()}</strong></span>
                    </div>
                  </>
                ) : <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Rate-limit data unavailable.</p>}
              </div>
            );
          })
      )}
    </div>
  );
}
